/**
 * §7.22 Feed Health — R8 redesign (fuses Sources / Coverage / Queue /
 * Alert-rule blocked-exclusion into one operator surface).
 *
 * Arrival pattern: the top-bar feed pill ("⚠ 3 / 71 live · 4 blocked")
 * routes here. The page is structured as 4 tabs:
 *
 *   Sources     — triage + audit modes for surveillance ingest, with
 *                 BLOCKED promoted as a separate band (never hidden)
 *   Coverage    — media feeds (GDELT etc.), distinct from surveillance
 *   Queue       — GDELT batch rollup + cost transparency widget
 *   Alert rules — BLOCKED-exclusion affordance for source.state rules
 *
 * Region lens (WHO regions) filters the Sources tab (matches the §7.23
 * persona-lens pattern). Tab badges count "needs attention" only —
 * degraded + rotting + blocked (Sources) and dead-letter (Queue).
 *
 * The Sources tab is sample-data-driven in v1 (per-source 30-day
 * history isn't persisted yet — that's Phase 7.2 with the snapshot
 * versioning work). When live ingest provides per-source history we
 * swap the data source; the SourcesView component contract doesn't
 * change.
 */

import { useEffect, useMemo, useState } from 'react';
import { SourcesView } from '../feed-health/SourcesView';
import { CoverageView } from '../feed-health/CoverageView';
import { QueueView } from '../feed-health/QueueView';
import { AlertExclusion } from '../feed-health/AlertExclusion';
import { LicenseReadinessView } from '../feed-health/LicenseReadinessView';
import { SovereigntyView } from '../feed-health/SovereigntyView';
import { SAMPLE_SOURCES, SAMPLE_QUEUE, TOTAL_REAL_SOURCES } from '../feed-health/sample-data';
import { LIVENESS } from '../catalog';
import { WHO_REGIONS } from '../feed-health/types';
import { summariseLicenseReadiness } from '@biomax/plugin-surveillance-feed';

type Tab = 'sources' | 'coverage' | 'queue' | 'alerts' | 'licenses' | 'sovereignty';

const TABS: ReadonlyArray<{ k: Tab; label: string }> = [
  { k: 'sources',     label: 'Sources' },
  { k: 'coverage',    label: 'Coverage' },
  { k: 'queue',       label: 'Queue' },
  { k: 'alerts',      label: 'Alert rules' },
  { k: 'licenses',    label: 'Licenses' },
  { k: 'sovereignty', label: 'Sovereignty' },
];

const VALID_TABS: ReadonlySet<Tab> = new Set(TABS.map(t => t.k));

/** CD R11 #5: deep-link entry — read `?tab=<name>` off the URL hash query.
 *  When absent or unrecognised, fall back to the default 'sources' tab.
 *  Same hash-query pattern Country Profile uses for `?iso3=`. */
function readTabFromHash(): Tab | null {
  if (typeof window === 'undefined') return null;
  const q = window.location.hash.split('?')[1] ?? '';
  const params = new URLSearchParams(q);
  const raw = params.get('tab');
  if (raw && VALID_TABS.has(raw as Tab)) return raw as Tab;
  return null;
}

export function FeedHealth() {
  const [tab, setTab] = useState<Tab>(() => readTabFromHash() ?? 'sources');
  const [region, setRegion] = useState<string>('all');

  // Re-sync if the hash query changes while this surface is mounted (e.g.
  // operator clicks the sovereignty deep-link on the feed-pill while
  // Feed Health is already open). Only updates when a valid `tab` is
  // present — other surfaces shouldn't clobber state here.
  useEffect(() => {
    const onHash = () => {
      const next = readTabFromHash();
      if (next) setTab(next);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const badges = useMemo(() => {
    const needAtt = SAMPLE_SOURCES.filter(s => s.state === 'degraded' || s.state === 'rotting').length;
    const blocked = SAMPLE_SOURCES.filter(s => s.state === 'blocked').length;
    const dead = SAMPLE_QUEUE.reduce((a, q) => a + q.dead, 0);
    // License badge = count of sources blocking paid launch (everything ≠ cleared).
    // This is the same number the License-readiness tab leads with.
    const licenseSummary = summariseLicenseReadiness();
    return { sources: needAtt + blocked, queue: dead, licenses: licenseSummary.blockingIds.length };
  }, []);

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.22</span>
        <h1 className="surface__title">Feed Health</h1>
      </div>
      <div className="surface__sub" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span>
          Operational triage for the ingest pipeline — sources, coverage feeds, GDELT batch queue, alert-rule exclusions in one place.
        </span>
        <span style={{ fontSize: 12, color: 'var(--bm-text-subtle)' }}>
          ↩ arrived via the top-bar feed pill
        </span>
      </div>

      {/* Liveness headline — the honest, continuously-checked "N of M live RIGHT
          NOW", from the committed docs/liveness snapshot the 6h ingest cron
          produces. Replaces the old blanket "ILLUSTRATIVE SAMPLE DATA" banner:
          the Sources tab below is still sample-driven (per-source 30-day history
          isn't persisted yet), but the liveness COUNT here is real. */}
      <LivenessHeadline />

      {/* The per-source Sources tab below is still illustrative until the live
          per-source history register is wired into the view. */}
      <div style={{
        marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 8,
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
        padding: '2px 8px', borderRadius: 4,
        color: 'var(--bm-state-stale)',
        background: 'var(--bm-prov-stale-bg)',
        border: '1px solid var(--bm-state-stale)',
      }}>⚠ Sources / Coverage / Queue tabs below — illustrative sample data</div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        marginTop: 14, marginBottom: 4,
      }}>
        <RegionLens region={region} setRegion={setRegion} />
        <span style={{ flex: 1 }} />
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)',
        }}>
          {TOTAL_REAL_SOURCES} sources tracked · {SAMPLE_SOURCES.length} rendered in sample
        </span>
      </div>

      <div style={{
        marginTop: 10,
        background: 'var(--bm-bg)',
        border: '1px solid var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
        overflow: 'hidden',
      }}>
        <nav style={{
          display: 'flex', gap: 0,
          borderBottom: '1px solid var(--bm-border)',
          background: 'var(--bm-bg-subtle)',
        }}>
          {TABS.map(t => {
            const badge = t.k === 'sources' ? badges.sources
              : t.k === 'queue' ? badges.queue
              : t.k === 'licenses' ? badges.licenses
              : 0;
            const isOn = tab === t.k;
            return (
              <button
                key={t.k}
                type="button"
                onClick={() => setTab(t.k)}
                style={{
                  padding: '10px 14px',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: isOn ? 'var(--bm-bg)' : 'transparent',
                  border: 'none',
                  borderBottom: `2px solid ${isOn ? 'var(--bm-accent, #6ea3ff)' : 'transparent'}`,
                  fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
                  color: isOn ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
                  cursor: 'pointer',
                }}
              >
                {t.label}
                {badge > 0 && (
                  <span style={{
                    display: 'inline-grid', placeItems: 'center',
                    minWidth: 18, height: 17, padding: '0 5px',
                    borderRadius: 9,
                    fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
                    background: 'var(--bm-state-degraded)',
                    color: 'var(--bm-bg)',
                  }}>{badge}</span>
                )}
              </button>
            );
          })}
        </nav>

        <div style={{ padding: 16 }}>
          {tab === 'sources'     && <SourcesView sources={SAMPLE_SOURCES} region={region} />}
          {tab === 'coverage'    && <CoverageView />}
          {tab === 'queue'       && <QueueView />}
          {tab === 'alerts'      && <AlertExclusion />}
          {tab === 'licenses'    && <LicenseReadinessView />}
          {tab === 'sovereignty' && <SovereigntyView />}
        </div>
      </div>
    </div>
  );
}

function RegionLens({ region, setRegion }: { region: string; setRegion: (r: string) => void }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '6px 10px',
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-sm)',
    }}>
      <span style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
        letterSpacing: '.4px', textTransform: 'uppercase',
        color: 'var(--bm-text-muted)',
      }}>Region</span>
      <select
        value={region}
        onChange={e => setRegion(e.target.value)}
        style={{
          padding: '3px 6px',
          fontFamily: 'var(--bm-font-mono)', fontSize: 11.5,
          background: 'var(--bm-bg)', color: 'var(--bm-text)',
          border: '1px solid var(--bm-border)',
          borderRadius: 4,
        }}
      >
        <option value="all">All regions</option>
        {WHO_REGIONS.filter(r => r !== 'Global').map(r => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>
    </div>
  );
}

/**
 * Liveness headline — the honest, continuously-checked "N of M sources live
 * right now", from the committed liveness snapshot the 6-hourly ingest cron
 * produces (docs/liveness + bundled copy). This is REAL data (reachability
 * metadata), distinct from the still-illustrative per-source Sources tab.
 *
 * `live` = flowing fresh · `aging` = reachable but stale · `down` = failed/empty
 * · `blocked` = staged (sanctions/antibot/geo/partnership — NOT a failure).
 */
function LivenessHeadline() {
  if (!LIVENESS) {
    return (
      <div style={{
        marginTop: 12, padding: '10px 13px',
        background: 'var(--bm-bg-subtle)',
        border: '1px dashed var(--bm-border)',
        borderRadius: 'var(--bm-radius-sm)',
        fontSize: 12.5, lineHeight: 1.5, color: 'var(--bm-text-muted)',
      }}>
        <b style={{ color: 'var(--bm-text)' }}>Liveness — pending first scheduled ingest.</b>{' '}
        The <code style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11 }}>pacc-ingest</code> cron
        (every 6h) populates the real, dated "N of M live" count here. We never
        seed a fabricated "all live" — an honest "pending" beats a flattering lie.
      </div>
    );
  }
  const L = LIVENESS;
  const reportable = L.total - L.staged;        // live/aging/down accounting excludes staged
  const pct = reportable > 0 ? Math.round((L.live / reportable) * 100) : 0;
  const cells: ReadonlyArray<{ label: string; n: number; color: string; note: string }> = [
    { label: 'Live',    n: L.live,    color: 'var(--bm-prov-confirmed)', note: 'flowing fresh now' },
    { label: 'Aging',   n: L.aging,   color: 'var(--bm-state-stale)',    note: 'reachable, data stale' },
    { label: 'Down',    n: L.down,    color: 'var(--bm-state-degraded)', note: 'supposed-to-be-live source failed this run' },
    { label: 'Staged',  n: L.staged,  color: 'var(--bm-prov-synthetic)', note: 'not-yet-live-by-design: gate-blocked OR wire-ready (URL/partner pending) — NOT a failure; event-level covered via WHO/HealthMap' },
  ];
  return (
    <div style={{
      marginTop: 12, padding: 12,
      background: 'var(--bm-bg-panel, var(--bm-bg))',
      border: '1px solid var(--bm-border)',
      borderLeft: '3px solid var(--bm-prov-confirmed)',
      borderRadius: 'var(--bm-radius-md)',
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--bm-prov-confirmed)' }}>
          {L.live} <span style={{ fontSize: 13, color: 'var(--bm-text-muted)' }}>of {reportable} sources live</span>
        </span>
        <span style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-subtle)' }}>
          {pct}% · {L.staged} staged · as of {L.generatedAt.slice(0, 16).replace('T', ' ')}Z
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {cells.map(c => (
          <span key={c.label} title={c.note} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 9px', borderRadius: 5,
            fontFamily: 'var(--bm-font-mono)', fontSize: 11,
            border: `1px solid ${c.color}`, color: c.color,
            background: 'var(--bm-bg-subtle)',
          }}>
            <b>{c.n}</b> {c.label}
          </span>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 11.5, lineHeight: 1.5, color: 'var(--bm-text-subtle)' }}>
        Continuously checked every 6h; track record at <code style={{ fontFamily: 'var(--bm-font-mono)', fontSize: 10.5 }}>docs/liveness/history.ndjson</code>.
        <b style={{ color: 'var(--bm-text-muted)' }}> Staged ≠ dark</b> — a staged national feed (gate-blocked or wire-ready)
        is covered at event-level by the live multilateral sources (WHO&nbsp;DON / regional bulletins / HealthMap). See the Sovereignty tab for per-source interim coverage.
      </div>
    </div>
  );
}
